/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE COACH'S COMMENT TRAVELS INTO NEXT WEEK — the read rule (W82 §6 item 4, the pro web step 4). PURE.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The TWIN of `HybridTrainer/Services/CoachCommentRule.swift` — THE SWIFT IS THE RULE. Held equal to it on every case of
 * `__fixtures__/coachCommentCorpus.json`, which the Swift writes (`coachCommentCorpus.test.ts`, the W66 method).
 *
 * On an exercise, the client sees the coach's LATEST comment on that library exercise if it was written after they last
 * PERFORMED it (a finished session, other than the one being shown, finished no later than `before`, holding the
 * exercise with a set that is not `skipped`). So a comment appears the next time the exercise comes round — in every
 * review mode, because nothing is copied onto next week's rows — and is consumed by doing it; a newer one replaces it.
 * A tie on `createdAt` breaks on the id, descending. Instants are milliseconds on both sides.
 */

export type CommentMark = { readonly id: string; readonly coachExerciseId: string; readonly createdAt: string; readonly body: string };
export type CommentSet = { readonly status: string; readonly completedAt: string | null };
export type CommentSessionExercise = { readonly coachExerciseId: string | null; readonly sets: readonly CommentSet[] };
export type CommentSession = { readonly id: string; readonly completedAt: string | null; readonly exercises: readonly CommentSessionExercise[] };

const ms = (iso: string): number => Date.parse(iso);

/** When a session finished: its `completed_at`, else its latest logged set's time; null = never finished. */
export function sessionFinish(s: CommentSession): number | null {
  if (s.completedAt !== null) return ms(s.completedAt);
  let best: number | null = null;
  for (const e of s.exercises) for (const x of e.sets) if (x.completedAt !== null) {
    const t = ms(x.completedAt);
    if (best === null || t > best) best = t;
  }
  return best;
}

/** Whether the session PERFORMED the exercise: a row of it with at least one set that is not `skipped`. */
export function performedIn(coachExerciseId: string, s: CommentSession): boolean {
  return s.exercises.some((e) => e.coachExerciseId === coachExerciseId && e.sets.some((x) => x.status !== "skipped"));
}

/** The last time, before this one, that the client performed the exercise (ms); null = never. */
export function lastPerformed(coachExerciseId: string, sessions: readonly CommentSession[], currentSessionId: string | null, before: number): number | null {
  let best: number | null = null;
  for (const s of sessions) {
    if (s.id === currentSessionId || !performedIn(coachExerciseId, s)) continue;
    const f = sessionFinish(s);
    if (f === null || f > before) continue;
    if (best === null || f > best) best = f;
  }
  return best;
}

/** The comment the client sees on this exercise, or null. */
export function coachCommentFor(
  coachExerciseId: string, comments: readonly CommentMark[], sessions: readonly CommentSession[],
  currentSessionId: string | null, before: number,
): CommentMark | null {
  const since = lastPerformed(coachExerciseId, sessions, currentSessionId, before);
  let best: CommentMark | null = null;
  for (const c of comments) {
    if (c.coachExerciseId !== coachExerciseId) continue;
    if (since !== null && !(ms(c.createdAt) > since)) continue;
    if (best === null) { best = c; continue; }
    const a = ms(c.createdAt), b = ms(best.createdAt);
    if (a > b || (a === b && c.id.toLowerCase() > best.id.toLowerCase())) best = c;
  }
  return best;
}
